import { Component, ViewChild } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ConfirmationService, MessageService, SelectItem } from "primeng/api";
import { Message } from "@bbwt/classes";
import { emailMaxLengthValidator, notEmptyValidator, ValidationPatterns } from "@bbwt/modules/validation";
import { FilterInputType, FilterType, IFilterSettings, StringFilterMatchMode } from "@features/filter";
import { CreateMode, GridComponent, IGridColumn, IGridSettings, ITableSettings, UpdateMode } from "@features/grid";
import { UserService } from "./user.service";
import { AccountStatus } from "./account-status";
import { getDefaultPermissions, resolvePermissionsOnRoleChange } from "./default-permission";
import { IPermission } from "@main/permissions/permission";
import { UserHelperService } from "./user-helper.service";
import { CoreRole, IRole, RoleService } from "@main/roles";
import { createUserTitleOptions, UserAccess } from ".";
import { SquadType } from "@project/models/squad";
import { HistoricalItem } from "@mipro/classes/historical-item";
import { FormButtonsConfig } from "@mipro/components";
import { MultiSelect } from "primeng/multiselect";
import { MiProModules } from "@mipro/classes/mipro-modules";
import { IOrganization } from "@main/companies";
import { Permissions } from "@main/permissions/permissions";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { IGoverningBody } from "@project/governingbody/governingbody";
import { checkGenericLoginOrClinicalSafetyOfficerRole } from "@main/users/generic-login.utils";
import { UserLogin } from "./user-login";
import { PlayerService } from "@project/player/player.service";
import { OrganizationGroupControlComponent } from "@mipro/custom-controls/organization-group-control";
import { PermissionService } from "@main/permissions/permission.service";
import { UserFiltersService } from "./user-filters.service";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormioRole } from "@main/roles/core-role";
import { DefaultPermissions } from "./default-permissions";

@Component({
    selector: "users",
    templateUrl: "users.component.html",
    styleUrls: ["./users.component.scss"]
})
export class UsersComponent {
    private readonly hiddenRoles: string[] = [CoreRole.PPS, FormioRole.FormioExternalUser];

    private inviteUser: UserLogin;

    tableSettings: ITableSettings = {
        lazyLoadOnInit: false
    };

    gridSettings: IGridSettings = {
        createMode: CreateMode.Disabled,
        createFunc: () => {
            this.resetForm();
            this.orgGroupsComponent.resetSelection();
            this.inviteUser = <UserLogin>{ email: "" };
            this.displayInviteUserDialog = true;
        },
        deletingEnabled: false,
        updateLink: "/app/users/edit/:id",
        updateMode: this.updateuserEnabled(),
        additionalRowActions: [
            {
                primeIcon: "mi mi-button-remove",
                buttonClass: "p-button-rounded p-button-text p-button-danger",
                handler: (rowdata: UserLogin) => this.deleteWithConfirmation(() => this.userService.toggleDeleting(rowdata.id)),
                allowOffline: () => true,
                visible: (rowData: UserLogin) =>
                    rowData.accountStatus != AccountStatus.Deleted && this.userHelperService.hasPermission(Permissions.ArchiveUser)
            },
            {
                primeIcon: "pi pi-history",
                buttonClass: "p-button-rounded p-button-secondary p-button-text",
                handler: async (rowData: UserLogin) => {
                    this.historicalItems = await this.userService.getHistoricalItems(rowData.userId);
                    this.showHistorical = true;
                },
                visible: () =>
                    this.userHelperService.isSuperAdmin ||
                    this.userHelperService.hasPermission(Permissions.CreateUser, Permissions.ArchiveUser)
            }
        ]
    };

    formButtonsConfig = <FormButtonsConfig>{
        saveLabel: "Invite",
        saveFunc: () => {
            this.invite(this.inviteUserForm.value);
        }
    };

    saving = false;

    filterSettings: IFilterSettings[];
    inviteUserForm: UntypedFormGroup;
    companiesOptions: Array<SelectItem> = [];
    rolesOptions: Array<SelectItem> = [];
    displayInviteUserDialog: boolean;
    permissionOptions: IPermission[];
    filterVisible: boolean = false;

    private defaultPermissions: DefaultPermissions;
    private organizationFormPermissions: { [orgId: string]: IPermission[] } = {};
    private lastRoleDefaultPermissionIds = new Set<string>();

    permissionsDependencies: { [permission: string]: string[][] };
    defaultSquadType = SquadType.Staff;
    showHistorical = false;
    historicalItems: HistoricalItem[] = [];
    isGenericLogin = false;
    isClinicalSafetyOfficer = false;
    companies: IOrganization[];
    appRoles: IRole[];
    govBodies: IGoverningBody[];
    govBodyOptions: Array<SelectItem> = [];

    @ViewChild(GridComponent, { static: true }) private grid: GridComponent;

    @ViewChild("userPermissionsMultiselect")
    userPermisssionsMultiselectComponent: MultiSelect;

    @ViewChild(OrganizationGroupControlComponent) orgGroupsComponent: OrganizationGroupControlComponent;

    titleOptions = createUserTitleOptions();

    constructor(
        private userService: UserService,
        private roleService: RoleService,
        private fb: UntypedFormBuilder,
        private messageService: MessageService,
        private userFiltersService: UserFiltersService,
        private userHelperService: UserHelperService,
        public namingConvService: NamingConventionService,
        private confirmationService: ConfirmationService,
        private playerService: PlayerService,
        broadcastService: BroadcastService,
        private permissionService: PermissionService
    ) {
        this.initWidgets();
        broadcastService
            .on(UserHelperService.ClaimsUpdatedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                if (this.grid) {
                    this.grid.setTableProperty("first", 0);
                    this.grid.reload();
                }
            });
    }

    addUser(): void {
        this.initForm();
        this.inviteUser = <UserLogin>{ email: "" };
        this.displayInviteUserDialog = true;
    }

    async invite(formValues) {
        if (this.saving) {
            return;
        }

        this.saving = true;
        this.inviteUser = <UserLogin>formValues;

        let errorMessage = "";

        try{
            await this.userService.sendInvite(this.inviteUser)
        } catch(error){
            errorMessage = error.error
        }

        this.saving = false;

        if(errorMessage != ""){
            if(errorMessage.includes("Email rejected")) {
                this.messageService.add(Message.Warning("Login created successfully.\nFailed to send invitation due to an invalid email.", "User Invitation"));
            } else {
                if(errorMessage.match(/^User with email (.)+ already exists in organisation (.)+$/)){
                    this.messageService.add(Message.Error(errorMessage, "Error"));
                } else if (errorMessage.match(/^Email already in use, please update and try again$/)) {
                    this.messageService.add(Message.Error(errorMessage, "Error"));
                }
                return;
            }
        } else {
            this.messageService.add(Message.Success("The invitation has been sent.", "User Invitation"));
        }

        this.displayInviteUserDialog = false;
        this.userPermisssionsMultiselectComponent.hide();
        this.grid.reload();
    }

    setDefaultPermissions(resetPermissions: boolean) {
        let roleDefaults =
            getDefaultPermissions(
                this.inviteUserForm.value,
                this.permissionOptions,
                this.defaultPermissions.defaultPermissionsByRole,
                this.messageService,
                true
            ) || [];

        if (this.shouldIncludeDefaultFormsPermissions()) {
            roleDefaults = this.mergeDefaultFormsPermissions(roleDefaults);
        }

        const { permissions, nextDefaultPermissionIds } = resolvePermissionsOnRoleChange(
            roleDefaults,
            this.inviteUserForm.get("permissions")?.value,
            this.lastRoleDefaultPermissionIds,
            resetPermissions,
            (this.userRoles ?? []).length > 0
        );

        this.lastRoleDefaultPermissionIds = nextDefaultPermissionIds;
        this.inviteUserForm.patchValue({ permissions });
    }

    private shouldIncludeDefaultFormsPermissions(): boolean {
        if (!this.isFormsEnabledForSelectedOrg()) {
            return false;
        }

        const roles = this.userRoles ?? [];
        if (roles.length === 0) {
            return false;
        }

        const disabledRolesForForms: string[] = [
            CoreRole.GenericLogin,
            CoreRole.GenericPatientLogin,
            CoreRole.ClinicalSafetyOfficer
        ];
        if (roles.every((role: IRole) => disabledRolesForForms.includes(role.name))) {
            return false;
        }

        return roles.some((role: IRole) => role.name !== CoreRole.OrganisationAdmin);
    }

    private isFormsEnabledForSelectedOrg(): boolean {
        const orgId = this.inviteUserForm?.get("organizationId")?.value;
        return this.companies?.find(organization => organization.id === orgId)?.isFormsEnabled ?? false;
    }

    private mergeDefaultFormsPermissions(permissions: IPermission[]): IPermission[] {
        const formPermissions = [Permissions.WriteFormsInstances, Permissions.ReadFormsInstances]
            .map(name => this.permissionOptions.find(permission => permission.name === name))
            .filter((permission): permission is IPermission => !!permission);

        const mergedPermissions = [...permissions];
        for (const formPermission of formPermissions) {
            if (!mergedPermissions.some(permission => permission.id === formPermission.id)) {
                mergedPermissions.push(formPermission);
            }
        }

        return mergedPermissions;
    }

    createRoleOptions(roles: IRole[]): void {
        this.rolesOptions = roles
            .filter(r =>
                (r.name !== CoreRole.GovBodyAdmin || this.userHelperService.isMiProAdmin || this.userHelperService.isSuperAdmin) &&
                !this.hiddenRoles.includes(r.name)
            )
            .map(roleItem => <SelectItem>{ label: roleItem.displayName, value: roleItem });
    }

    private async initWidgets(): Promise<void> {
        this.gridSettings.dataService = this.userService;

        this.initForm();

        const { organizations, permissionDependencies, governingBodies } = await this.userFiltersService.loadUserFilters();
        this.companies = organizations;
        this.companiesOptions = organizations.map(
            company => <SelectItem>{ label: `${company.governingBodyName} - ${company.name}`, value: company.id }
        );
        this.govBodies = governingBodies;
        this.permissionsDependencies = permissionDependencies;

        await this.initDefaultPermissions();
        await this.updateRoles();
        this.createRoleOptions(this.appRoles);

        if (governingBodies) {
            //GovBodies will only be initilized for SuperAdmin or MiPro Admin
            this.govBodyOptions = this.govBodies.map(gB => <SelectItem>{ label: gB.name, value: gB.id });
        }

        this.updateFormPermissionDependencies();

        this.tableSettings = {
            columns: <IGridColumn[]>[
                { field: "userName", header: "Email/Username" },
                { field: "firstName", header: "First Name" },
                { field: "lastName", header: "Last Name" },
                {
                    field: "accountStatus",
                    header: "Status",
                    sortable: false,
                    displayHandler: value => AccountStatus[value]
                },
                {
                    field: "userAccesses",
                    header: "User Types",
                    sortable: false,
                    displayHandler: (userAccesses: UserAccess[]) => {
                        const roles = {};
                        userAccesses
                            .filter(ua => !ua.isArchived)
                            .forEach(ua => ua.roles.forEach(r => (roles[r.displayName] = 1)));

                        return Object.getOwnPropertyNames(roles).filter(role => !this.hiddenRoles.includes(role)).join(", ");
                    }
                }
            ],
            selectionMode: "multiple",
            stateKey: "users-grid",
            stateStorage: "session",
            sortField: "email"
        };

        this.filterSettings = <IFilterSettings[]>[
            {
                header: "Email/Username",
                valueFieldName: "userName",
                matchModeSelectorVisible: false,
                matchMode: StringFilterMatchMode.Contains
            },
            {
                header: "First name",
                valueFieldName: "firstName",
                matchModeSelectorVisible: false,
                matchMode: StringFilterMatchMode.Contains
            },
            {
                header: "Last name",
                valueFieldName: "lastName",
                matchModeSelectorVisible: false,
                matchMode: StringFilterMatchMode.Contains
            },
            {
                header: "Gov. Body Name",
                inputType: FilterInputType.Dropdown,
                dropdownOptions: this.govBodyOptions,
                valueFieldName: "governingBodyId",
                visible: () => this.userHelperService.isMiProAdmin || this.userHelperService.isSuperAdmin,
                autoSubmitSelectableInputChange: true
            },
            {
                header: this.namingConvService.organisationSingular,
                inputType: FilterInputType.Multiselect,
                dropdownOptions: this.companiesOptions,
                valueFieldName: "organizationId",
                visible: () => this.userHelperService.isMiProAdmin || this.userHelperService.isSuperAdmin,
                autoSubmitSelectableInputChange: true
            },
            {
                header: "Status",
                inputType: FilterInputType.Multiselect,
                dropdownOptions: this.userService.getAccountStatuses().map(o => ({ label: AccountStatus[o], value: o })),
                valueFieldName: "accountStatus",
                filterType: FilterType.Numeric,
                defaultValue: [AccountStatus.Active],
                autoSubmitSelectableInputChange: true
            },
            {
                header: "User Types",
                inputType: FilterInputType.Dropdown,
                dropdownOptions: this.appRoles.map(x => <SelectItem>{ label: x.displayName, value: x.id }),
                valueFieldName: "roles",
                autoSubmitSelectableInputChange: true
            }
        ];

        this.onOrganizationChanged();
    }

    get roles() {
        return CoreRole;
    }

    get userName() {
        return this.inviteUserForm.get("userName");
    }

    get userRoles() {
        return this.inviteUserForm.get("roles").value;
    }

    get isGoverningBodyAdmin(): boolean {
        return this.userRoles.some(r => r.name == CoreRole.GovBodyAdmin);
    }

    get isInvalidGovBodyAdmin() {
        return this.isGoverningBodyAdmin && !this.inviteUserForm.get("governingBody").value;
    }

    updateDisabledRoles() {
        if (this.userRoles.length == 0) {
            this.rolesOptions = this.rolesOptions.map(r => {
                return { ...r, disabled: false };
            });
        } else {
            if (this.isGoverningBodyAdmin) {
                this.rolesOptions = this.rolesOptions.map(r => {
                    return { ...r, disabled: r.label != CoreRole.GovBodyAdmin };
                });
            } else {
                this.rolesOptions = this.rolesOptions.map(r => {
                    return { ...r, disabled: r.label == CoreRole.GovBodyAdmin };
                });
            }
        }
    }

    userTypeChanged(resetPermissions = false) {
        this.updateDisabledRoles();

        [this.isGenericLogin, this.isClinicalSafetyOfficer] = checkGenericLoginOrClinicalSafetyOfficerRole(
            this.userName,
            this.inviteUserForm.get("roles").value
        );

        this.setDefaultPermissions(resetPermissions || this.isGenericLogin || this.isClinicalSafetyOfficer);
        this.userPermisssionsMultiselectComponent?.options?.forEach(x => (x.disabled = false));

        if (this.isClinicalSafetyOfficer || this.isGenericLogin) {
            this.userPermisssionsMultiselectComponent?.options?.forEach(x => {
                if (
                    x.name === Permissions.ReadFormsInstances ||
                    x.name === Permissions.WriteFormsDesign ||
                    x.name === Permissions.WriteFormsDesign
                ) {
                    x.disabled = true;
                }
            });
        }

        this.permissionOptions = this.createPermissionOptions();
    }

    showAddButton() : boolean {
        if(this.addUserEnabled() == CreateMode.Dialog) {
            return true;
        }
        return false;
    }

    addUserEnabled(): CreateMode {
        return this.userHelperService.hasPermission(Permissions.CreateUser) ? CreateMode.Dialog : CreateMode.Disabled;
    }

    updateuserEnabled(): UpdateMode {
        return this.userHelperService.hasPermission(Permissions.CreateUser) ? UpdateMode.Redirect : UpdateMode.Disabled;
    }

    private initForm(): void {
        this.inviteUserForm = this.fb.group({
            userName: [""],
            email: ["", [Validators.required, Validators.pattern(ValidationPatterns.email), emailMaxLengthValidator]],
            title: [null, Validators.required],
            firstName: ["", [notEmptyValidator()]],
            lastName: ["", [notEmptyValidator()]],
            organizationId:
                this.userHelperService.isMiProAdmin || this.userHelperService.isSuperAdmin
                    ? [null, Validators.required]
                    : [this.userHelperService.hashedOrganizationId],
            organizationGroups: [[], Validators.required],
            governingBody: [null],
            roles: [[], [this.validateGenericLoginRole, Validators.required]],
            permissions: [[], Validators.required],
            dateOfBirth: [null, Validators.required]
        });
    }

    private resetForm(): void {
        this.inviteUserForm.patchValue({
            userName: "",
            email: "",
            firstName: "",
            lastName: "",
            title: null,
            organizationId:
                this.userHelperService.isMiProAdmin || this.userHelperService.isSuperAdmin
                    ? null
                    : this.userHelperService.hashedOrganizationId,
            roles: [],
            permissions: [],
            dateOfBirth: null,
            governingBody: null
        });

        this.isGenericLogin = false;
        this.isClinicalSafetyOfficer = false;
        this.lastRoleDefaultPermissionIds = new Set();
        this.userName.clearValidators();

        this.inviteUserForm.markAsPristine();
        this.inviteUserForm.markAsUntouched();
    }

    private get validateGenericLoginRole() {
        return (control: AbstractControl) => {
            const roles: IRole[] = control.value;
            if (roles?.length > 1 && roles.some(r => CoreRole.GenericLogin === r.name)) {
                return { genericLoginRole: true };
            }
            if (roles?.length > 1 && roles.some(r => CoreRole.ClinicalSafetyOfficer === r.name)) {
                return { clinicalSafetyOfficerRole: true };
            }
            if (roles?.length > 1 && roles.some(r => CoreRole.GenericPatientLogin === r.name)) {
                return { genericPatientRole: true };
            }
            if (roles?.length > 1 && roles.some(r => FormioRole.FormioExternalUser === r.name)) {
                return { formioExternalUser: true };
            }
            return null;
        };
    }

    async onGovBodyChanged() {
        const govBodyId = this.inviteUserForm.get("governingBody").value;

        if (govBodyId) {
            const orgId = this.companies.find(c => c.governingBodyId == govBodyId).id;

            this.inviteUserForm.patchValue({
                organizationId: orgId
            });

            await this.onOrganizationChanged();

            const newGovBodyRole = this.rolesOptions.filter(r => r.label == "Governing Body Admin").map(r => r.value);

            this.inviteUserForm.patchValue({
                roles: newGovBodyRole
            });

            this.userTypeChanged();

            const groupsId = (await this.playerService.getOrganizationGroups(orgId))[0].groups
                .filter(g => g.name != "Unassigned Patients")
                .map(g => g.id)
                .slice(0, 1);

            this.inviteUserForm.patchValue({
                organizationGroups: groupsId
            });
        }
    }

    async onOrganizationChanged() {
        await this.updateRoles();
        await this.cacheOrganizationFormPermissions();

        this.lastRoleDefaultPermissionIds = new Set();
        this.inviteUserForm.patchValue({
            roles: [],
            permissions: []
        });
        const org = this.inviteUserForm.get("organizationId").value;
        const orgData = this.companies.find(c => c.id === org);
        if (orgData === undefined) {
            this.createRoleOptions(this.appRoles);
            return;
        }

        const isRavenEnabled = orgData.isRavenEnabled;
        const isEventsEnabled = orgData.isEventsEnabled;
        const isDiaryEnabled = orgData.isDiaryEnabled;
        const isFunctionalGut = orgData.isFunctionalGut;
        const isFormsEnabled = orgData.isFormsEnabled;

        this.permissionOptions = this.createPermissionOptions();
        if (!isFunctionalGut) {
            this.permissionOptions = this.permissionOptions.filter(
                x => x.name !== Permissions.WriteFunctionalGut && x.name !== Permissions.ReadFunctionalGut
            );
        }

        if (!isFormsEnabled) {
            this.permissionOptions = this.permissionOptions.filter(
                x =>
                    x.name !== Permissions.ReadFormsInstances &&
                    x.name !== Permissions.WriteFormsInstances &&
                    x.name !== Permissions.WriteFormsDesign
            );
        }

        const role = this.inviteUserForm.get("roles").value[0];

        let filteredRoles = this.appRoles;
        if (!isRavenEnabled && !isEventsEnabled) {
            filteredRoles = filteredRoles.filter(x => x.name !== CoreRole.ClinicalSafetyOfficer);
            if (role !== undefined && role.name === CoreRole.ClinicalSafetyOfficer) {
                this.inviteUserForm.patchValue({
                    roles: [],
                    permissions: []
                });
            }
        }
        if (!isDiaryEnabled) {
            filteredRoles = filteredRoles.filter(x => x.name !== CoreRole.GenericLogin && x.name !== CoreRole.GenericPatientLogin);
            if (role !== undefined && (role.name === CoreRole.GenericLogin || role.name === CoreRole.GenericPatientLogin)) {
                this.inviteUserForm.patchValue({
                    roles: [],
                    permissions: []
                });
            }
        }

        this.createRoleOptions(filteredRoles);
        this.userTypeChanged(true);
    }

    onUserPermissionChange(item: { itemValue: { id: string; name: string; id_original: number } }) {
        if (!item.itemValue) return;

        const isItemRemoved = !this.userPermisssionsMultiselectComponent.value.includes(item.itemValue);
        this.triggerDependenciesForItem(item.itemValue.name, isItemRemoved);
    }

    private triggerDependenciesForItem(permissionName: string, isRemoved: boolean) {
        let options = this.userPermisssionsMultiselectComponent.options;
        let values = this.userPermisssionsMultiselectComponent.value;

        if (!this.permissionsDependencies[permissionName]) {
            return;
        }

        const dependentPermissionsFamilies = this.permissionsDependencies[permissionName];

        const permissions = getDefaultPermissions(
            this.inviteUserForm.value,
            this.permissionOptions,
            this.defaultPermissions.defaultPermissionsByRole,
            this.messageService,
            true
        );

        const dependentPermissionsNames = [];
        dependentPermissionsFamilies.forEach(element => {
            let permission = element.length != 1 ? element.find(x => permissions.find(y => y.name == x)) ?? element[0] : element[0];
            if (permission) {
                dependentPermissionsNames.push(permission);
            }
        });

        let valuesToChange = this.userPermisssionsMultiselectComponent.options.filter(x => dependentPermissionsNames.includes(x.name));

        if (isRemoved) {
            options
                .filter(option => dependentPermissionsFamilies.flatMap(x => x).some(value => option.name == value))
                .forEach(option => (option.disabled = false));
        } else {
            let valuesToAdd = valuesToChange.filter(x => !values.some(value => value.id == x.id));
            values.push(...valuesToAdd);

            options.filter(option => valuesToChange.some(value => option.id == value.id)).forEach(option => (option.disabled = true));
        }

        this.userPermisssionsMultiselectComponent.options = options;
        this.userPermisssionsMultiselectComponent.writeValue(values);
    }

    private async updateRoles() {
        const organizationId = this.inviteUserForm.get("organizationId").value;
        this.appRoles = [
            ...(organizationId
                ? await this.roleService.getOrganizationRoles(organizationId)
                : await this.roleService.getCurrentOrganizationRoles())
        ];
    }

    private deleteWithConfirmation(onAccept: () => Promise<any>) {
        this.confirmationService.confirm({
            message: "Are you sure you want to remove this user?",
            accept: async () => {
                await onAccept();
                this.grid.reload();
            }
        });
    }

    private async initDefaultPermissions() {
        this.defaultPermissions = await this.userService.getDefaultPermissions();
        this.permissionOptions = this.createPermissionOptions();
    }

    private async cacheOrganizationFormPermissions() {
        const organizationId = this.inviteUserForm.get("organizationId").value;
        if (organizationId in this.organizationFormPermissions) {
            return;
        }

        const permissions = await this.permissionService.getOrganizationFormPermissions(organizationId);
        this.organizationFormPermissions[organizationId] = permissions;
        this.updateFormPermissionDependencies();
    }

    private updateFormPermissionDependencies() {
        const allFormPermissions = Object.keys(this.organizationFormPermissions).flatMap(
            (k: string) => this.organizationFormPermissions[k]
        );
        allFormPermissions
            .filter(fp => fp.dependantOnPermissionId)
            .forEach(fp => {
                const permDependentOn = allFormPermissions.find(fp2 => fp2.id == fp.dependantOnPermissionId);
                if (permDependentOn) {
                    this.permissionsDependencies[permDependentOn.name] = [[fp.name]];
                }
            });
    }

    private createPermissionOptions(): IPermission[] {
        const organizationId = this.inviteUserForm.get("organizationId").value;
        const currentOrg = this.companies.find(o => o.id === organizationId);
        const formPermissions = this.organizationFormPermissions[organizationId] ?? [];

        const enabledModules = Object.entries(currentOrg?.enabledModules ?? {})
            .filter(([_, enabled]) => enabled)
            .map(([module]) => module);

        const allPermissions = [
            ...this.defaultPermissions.allDefaultPermissions,
            ...formPermissions
        ];

        const filteredPermissions = allPermissions.filter(permission => {
            const modulesWithThisPermission = Object.entries(this.ModulePermissionMap)
                .filter(([_, perms]) => perms.includes(permission.name as Permissions))
                .map(([module]) => module);

            if (modulesWithThisPermission.length === 0) return true;

            return modulesWithThisPermission.every(m => enabledModules.includes(m));
        });

        const flatPermissionsDependencies = Object.keys(this.permissionsDependencies).flatMap(key =>
            this.permissionsDependencies[key]
                .flatMap(x => x)
                .map(permission => ({ masterPermission: key, dependentPermission: permission }))
        );

        const checkIfEnabledInPairWithMasterPermission = (dependentPermissionName: string): boolean => {
            const masterPermissionName = flatPermissionsDependencies.find(
                x => x.dependentPermission === dependentPermissionName
            )?.masterPermission;

            const userPermissionsNames = this.inviteUserForm.get("permissions")?.value.map(x => x.name) ?? [];
            return userPermissionsNames.includes(masterPermissionName) && userPermissionsNames.includes(dependentPermissionName);
        };

        return filteredPermissions.map(p => ({
            ...p,
            disabled: p.name === Permissions.RegisterPatient ||
                      checkIfEnabledInPairWithMasterPermission(p.name)
        }));
    }

    toggleFilter() {
        this.filterVisible = !this.filterVisible;
    }

    DAcreateFunc() {
        this.resetForm();
        this.orgGroupsComponent.resetSelection();
        this.inviteUser = <UserLogin>{ email: "" };
        this.displayInviteUserDialog = true;
    }


     private ModulePermissionMap: { [key in MiProModules]?: Permissions[] } = {
        [MiProModules.Consultations]: [
            Permissions.ConsultationHistoryReadUnrestricted,
            Permissions.ConsultationHistoryReadLimited,
            Permissions.ConsultationHistoryReadUnrestrictedExceptNotes,
            Permissions.ConsultationHistoryWrite,
            Permissions.ConsultationHistoryPrivateEpisodeRead,
            Permissions.ConsultationHistoryPrivateEpisodeWrite,
            Permissions.ConsultationHistoryReadPrivateEpisodesUnrestricted,
            Permissions.ConsultationHistoryWritePrivateEpisodesUnrestricted
        ],
        [MiProModules.Diary]: [
            Permissions.ReadDiary,
            Permissions.WriteDiary,
            Permissions.WriteIncidents
        ],
        [MiProModules.Prescriptions]: [
            Permissions.ReadPrescriptions,
            Permissions.WritePrescriptions
        ],
        [MiProModules.FunctionalGut]: [
            Permissions.ReadFunctionalGut,
            Permissions.WriteFunctionalGut
        ],
        [MiProModules.Forms]: [
            Permissions.ReadFormsInstances,
            Permissions.WriteFormsInstances,
            Permissions.WriteFormsDesign
        ],
        [MiProModules.Raven]: [
            Permissions.EditRavenConfig
        ],
        [MiProModules.GlobalDRO]: [
            Permissions.AccessGlobalDRO
        ],
        [MiProModules.NHS]: [
            Permissions.ReadNHSData
        ],
        [MiProModules.EventsModule]: [
            Permissions.WriteIncidents
        ],
        // Currently no permissions provided for these modules, but explicitly listed for clarity.
        [MiProModules.Tues]: [],
        [MiProModules.Referrals]: [],
        [MiProModules.Investigations]: [],
        [MiProModules.Reporting]: []
    };

}
